import { ComponentProps } from "react";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Breakdown } from "src/base-components/Breakdown";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { Icon } from "src/design-system/Icon";
import { JobRun } from "src/jobs/types";
import { formatNumber } from "src/utils/numbers";

export const DecisionsByStatus: React.FC<
  Pick<
    JobRun,
    | "success_rows_count"
    | "failed_rows_count"
    | "pending_rows_count"
    | "total_rows_count"
  > & {
    showProgressNumbers?: boolean;
  }
> = ({
  failed_rows_count,
  pending_rows_count,
  success_rows_count,
  total_rows_count,
  showProgressNumbers = false,
}) => {
  const processedCount =
    success_rows_count + pending_rows_count + failed_rows_count;

  const data = [
    {
      type: "success",
      displayText: "Successful",
      count: success_rows_count,
      color: "green",
    },
    {
      type: "pending",
      displayText: "Pending",
      count: pending_rows_count,
      color: "indigo",
    },
    {
      type: "failed",
      displayText: "Failed",
      count: failed_rows_count,
      color: "red",
    },
    {
      type: "not_started",
      displayText: "Waiting",
      count: total_rows_count - processedCount,
      color: "gray",
    },
  ] satisfies (ComponentProps<typeof Breakdown>["bars"][number] & {
    type: string;
    displayText: string;
  })[];
  // Satisfies allows us to complain with <Breakdown/> while
  // having the option of specifying additional fields
  // and don't get a TypeScript error

  return (
    <CustomPopover
      button={
        <div
          className="flex h-4 w-full cursor-pointer items-center gap-x-1.5"
          data-loc="run-decisions-by-status-button"
        >
          <div className="flex-1">
            <Breakdown bars={data} />
          </div>
          {showProgressNumbers && (
            <>
              <span data-loc="total-run-rows-count">
                {formatNumber(total_rows_count)}
              </span>
              <span
                className="text-gray-500"
                data-loc="run-rows-processed-percentage"
              >
                (
                {formatNumber(
                  total_rows_count !== 0
                    ? (success_rows_count +
                        failed_rows_count +
                        pending_rows_count) /
                        total_rows_count
                    : 0,
                  {
                    style: "percent",
                  },
                )}
                )
              </span>
            </>
          )}
        </div>
      }
      offsetX={24}
      placement="right"
      wrapperClassName="w-full"
    >
      <div className="relative w-122 cursor-default">
        <CustomPopover.Button
          className="absolute top-4 right-4"
          data-loc="close-decisions-by-status"
        >
          <Icon icon={faXmark} size="xs" />
        </CustomPopover.Button>
        <div className="border-gray-100 border-b px-5 pt-4 pb-3">
          <h1 className="text-left font-inter-semibold-16px text-gray-800">
            Decisions by status
          </h1>
        </div>
        <div className="px-5 pt-4 pb-6">
          <div className="border border-gray-100 text-left text-gray-800">
            <div className="flex h-8 bg-gray-50 font-inter-medium-12px">
              <div className="flex min-w-[127px] items-center border-gray-100 border-r px-2">
                Status
              </div>
              <div className="flex min-w-0 flex-1 items-center border-gray-100 border-r px-2">
                Decisions
              </div>
              <div className="flex min-w-[62px] items-center border-gray-100 border-r px-2"></div>
              <div className="flex min-w-[169px] items-center px-2"></div>
            </div>
            {data.map((entry) => (
              <div
                key={entry.type}
                className="flex h-8 border-gray-100 border-t font-inter-normal-12px"
              >
                <div className="flex min-w-[127px] items-center border-gray-100 border-r px-1.5">
                  <Pill
                    variant={
                      entry.color === "indigo" ? "indigo-light" : entry.color
                    }
                  >
                    <Pill.Text>{entry.displayText}</Pill.Text>
                  </Pill>
                </div>
                <div className="flex min-w-0 flex-1 items-center border-gray-100 border-r px-2">
                  <p
                    className="flex-1 text-right font-inter-normal-12px text-gray-800"
                    data-loc={`run-decisions-by-status-button-${entry.type}-count`}
                  >
                    {formatNumber(entry.count)}
                  </p>
                </div>
                <div className="flex min-w-[62px] items-center border-gray-100 border-r px-2">
                  <p
                    className="flex-1 text-right font-inter-normal-12px text-gray-500"
                    data-loc={`run-decisions-by-status-button-${entry.type}-percentage`}
                  >
                    (
                    {formatNumber(
                      total_rows_count !== 0
                        ? entry.count / total_rows_count
                        : 0,
                      {
                        style: "percent",
                      },
                    )}
                    )
                  </p>
                </div>
                <div className="flex min-w-[169px] items-center px-2">
                  <Breakdown bars={data} highlightOnly={entry.color} />
                </div>
              </div>
            ))}
            <div className="flex h-8 border-gray-100 border-t font-inter-normal-12px">
              <div className="flex min-w-[127px] items-center border-gray-100 border-r px-1.5 font-inter-normal-12px text-gray-800">
                Total
              </div>
              <div className="flex min-w-0 flex-1 items-center border-gray-100 border-r px-2">
                <p className="flex-1 text-right font-inter-semibold-13px text-gray-800">
                  {formatNumber(total_rows_count)}
                </p>
              </div>
              <div className="flex min-w-[62px] items-center border-gray-100 border-r px-2">
                <p className="flex-1 text-right font-inter-normal-12px text-gray-500">
                  (100%)
                </p>
              </div>
              <div className="flex min-w-[169px] items-center px-2">
                <Breakdown bars={data} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </CustomPopover>
  );
};
